import { axios, transformAxiosError } from "../utils/axios.create";
import {
  TreatmentAgency,
  TreatmentAgencyLocation,
  TreatmentAgencyStatus,
} from "../domain/treatmentAgencies.interface";

export type FindAllTreatmentAgenciesPayload = {
  search?: string;
  status?: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type TreatmentAgencyPayload = {
  name: string;
  status: TreatmentAgencyStatus;
  locations: TreatmentAgencyLocation[];
  createdBy?: string;
  updatedBy?: string;
};

export type TreatmentAgenciesListResponse = {
  list: TreatmentAgency[];
  totalCount: number;
};

const findAll = async ({
  search,
  status,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: FindAllTreatmentAgenciesPayload): Promise<TreatmentAgenciesListResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (search) {
    queryParams.append("search", search);
  }

  if (status) {
    queryParams.append("status", status);
  }

  return axios
    .get(`/treatment-agencies?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: body.list || [],
        totalCount: body.totalCount || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<TreatmentAgency> => {
  return axios
    .get(`/treatment-agencies/find/${id}`)
    .then((res) => {
      return res.data.body as TreatmentAgency;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (
  data: TreatmentAgencyPayload
): Promise<TreatmentAgency> => {
  return axios
    .post("/treatment-agencies", data)
    .then((res) => {
      return res.data.body as TreatmentAgency;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: TreatmentAgencyPayload;
}): Promise<TreatmentAgency> => {
  return axios
    .put(`/treatment-agencies/${id}`, data)
    .then((res) => {
      return res.data.body as TreatmentAgency;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/treatment-agencies/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const TreatmentAgenciesService = {
  findAll,
  findOne,
  create,
  update,
  archive,
};
